import EmblaCarousel from 'embla-carousel'

/**
 * Generic Embla-backed slider for any section.
 *
 * Exposes reactive state for optional pagination dots:
 *   - `snaps`: array of scroll-snap indices (length = number of "pages")
 *   - `selectedIndex`: currently-active snap index
 *   - `scrollTo(i)`: jump to snap i
 *
 * Dots are opt-in — sections render their own dots markup using these properties,
 * so sliders that don't need dots cost nothing extra.
 *
 * Usage with dots:
 *   <template x-for="(snap, i) in snaps" :key="i">
 *     <button @click="scrollTo(i)"
 *             :class="i === selectedIndex ? 'active-style' : 'inactive-style'"></button>
 *   </template>
 */
export default () => ({
  embla: null,
  snaps: [],
  selectedIndex: 0,

  init() {
    this.$nextTick(() => {
      const viewport = this.$refs.viewport
      if (!viewport) return

      // Default options preserve existing multi-item, free-scroll behavior.
      // Sections can override per-instance via a `data-embla-options` JSON attribute,
      // e.g. one-per-slide: data-embla-options='{"loop": true, "dragFree": false, "align": "center", "containScroll": false}'
      let options = {
        align: 'start',
        dragFree: true,
        containScroll: 'trimSnaps',
        loop: false,
        slidesToScroll: 1,
      }
      const raw = this.$el.dataset.emblaOptions
      if (raw) {
        try { options = { ...options, ...JSON.parse(raw) } }
        catch (e) { console.warn('slider: invalid data-embla-options', e) }
      }

      this.embla = EmblaCarousel(viewport, options)

      const syncState = () => {
        this.snaps = this.embla.scrollSnapList()
        this.selectedIndex = this.embla.selectedScrollSnap()
      }

      syncState()
      this.embla.on('select', () => { this.selectedIndex = this.embla.selectedScrollSnap() })
      this.embla.on('reInit', syncState)
    })
  },

  scrollTo(index) { if (this.embla) this.embla.scrollTo(index) },
  prev()          { if (this.embla) this.embla.scrollPrev() },
  next()          { if (this.embla) this.embla.scrollNext() },
  destroy()       { if (this.embla) this.embla.destroy() },
})
